(function () {
  'use strict';

  var PROPERTY_NAME = '_compare_at_price';
  var variantCache = new Map();
  var variantRequests = new Map();
  var originalFetch = window.fetch ? window.fetch.bind(window) : null;

  function getNumericId(id) {
    if (id == null) return null;
    var match = String(id).match(/(\d+)$/);
    return match ? match[1] : String(id);
  }

  function toCents(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && isFinite(value)) return Math.round(value);

    var stringValue = String(value).trim();
    if (!stringValue) return null;

    if (/^\d+$/.test(stringValue)) return Number(stringValue);

    var parsed = Number(stringValue.replace(/[^0-9.-]+/g, ''));
    return isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  function firstPresent() {
    for (var i = 0; i < arguments.length; i += 1) {
      if (arguments[i] != null && arguments[i] !== '') return arguments[i];
    }

    return null;
  }

  function normalizeVariant(variant) {
    if (!variant) return null;

    var id = getNumericId(variant.id || variant.variantId);
    if (!id) return null;

    var price = toCents(
      firstPresent(
        variant.price,
        variant.priceV2?.amount,
        variant.price_v2?.amount
      )
    );
    var compareAtPrice = toCents(
      firstPresent(
        variant.compare_at_price,
        variant.compareAtPrice,
        variant.compareAtPriceV2?.amount,
        variant.compare_at_price_v2?.amount
      )
    );

    return {
      id: id,
      price: price,
      compareAtPrice: compareAtPrice
    };
  }

  function registerVariant(variant) {
    var normalizedVariant = normalizeVariant(variant);
    if (!normalizedVariant) return null;

    variantCache.set(normalizedVariant.id, normalizedVariant);
    return normalizedVariant;
  }

  function registerProduct(product) {
    if (!product) return;

    var variants = product.variants;
    if (variants && !Array.isArray(variants) && Array.isArray(variants.nodes)) {
      variants = variants.nodes;
    }

    if (Array.isArray(variants)) {
      variants.forEach(registerVariant);
    }
  }

  function registerProductDataFromNode(node) {
    var rawProductData = node.matches?.('[data-product-data]')
      ? (node.tagName === 'SCRIPT' ? node.textContent : node.dataset.productData)
      : null;

    if (!rawProductData) return;

    try {
      registerProduct(JSON.parse(rawProductData));
    } catch (error) {
      // Product data can be owned by third-party widgets. Ignore invalid payloads.
    }
  }

  function scan(root) {
    var scanRoot = root || document;

    if (scanRoot.matches?.('[data-product-data]')) {
      registerProductDataFromNode(scanRoot);
    }

    scanRoot.querySelectorAll?.('[data-product-data]').forEach(registerProductDataFromNode);
  }

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function fetchVariant(variantId) {
    if (!originalFetch) return Promise.resolve(null);

    var normalizedId = getNumericId(variantId);
    if (!normalizedId) return Promise.resolve(null);

    if (variantCache.has(normalizedId)) {
      return Promise.resolve(variantCache.get(normalizedId));
    }

    if (!variantRequests.has(normalizedId)) {
      var rootUrl = getRootUrl();
      var variantUrl = rootUrl.replace(/\/?$/, '/') + 'variants/' + normalizedId + '.js';

      variantRequests.set(
        normalizedId,
        originalFetch(variantUrl)
          .then(function (response) {
            if (!response.ok) return null;
            return response.json();
          })
          .then(registerVariant)
          .catch(function () {
            return null;
          })
      );
    }

    return variantRequests.get(normalizedId);
  }

  function isSaleVariant(variant) {
    return Boolean(
      variant &&
        variant.compareAtPrice != null &&
        variant.price != null &&
        variant.compareAtPrice > variant.price
    );
  }

  function getPropertyValue(variant) {
    return (variant.compareAtPrice / 100).toFixed(2);
  }

  function ensurePropertiesObject(item) {
    if (!item.properties || typeof item.properties !== 'object' || Array.isArray(item.properties)) {
      item.properties = {};
    }

    return item.properties;
  }

  function registerVariantFromSource(source) {
    if (!source?.dataset) return;

    registerVariant({
      id: source.dataset.variantId || source.dataset.variant,
      price: source.dataset.price,
      compare_at_price: source.dataset.compareAtPrice
    });
  }

  async function enhanceCartItem(item, source) {
    if (!item || item.id == null) return item;

    registerVariantFromSource(source);

    var variant = variantCache.get(getNumericId(item.id)) || await fetchVariant(item.id);
    if (isSaleVariant(variant)) {
      ensurePropertiesObject(item)[PROPERTY_NAME] = getPropertyValue(variant);
    } else if (variant && item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)) {
      delete item.properties[PROPERTY_NAME];
    }

    return item;
  }

  async function enhanceJsonPayload(payload) {
    if (!payload || typeof payload !== 'object') return payload;

    if (Array.isArray(payload.items)) {
      await Promise.all(payload.items.map(function (item) {
        return enhanceCartItem(item);
      }));
    } else if (payload.id != null) {
      await enhanceCartItem(payload);
    }

    return payload;
  }

  async function enhanceFormData(formData) {
    if (!formData || typeof formData.get !== 'function') return formData;

    var variantId = formData.get('id');
    if (!variantId) return formData;

    var variant = variantCache.get(getNumericId(variantId)) || await fetchVariant(variantId);
    var propertyInputName = 'properties[' + PROPERTY_NAME + ']';

    if (isSaleVariant(variant)) {
      formData.set(propertyInputName, getPropertyValue(variant));
    } else if (typeof formData.delete === 'function') {
      formData.delete(propertyInputName);
    }

    return formData;
  }

  async function enhanceSearchParams(searchParams) {
    if (!searchParams || typeof searchParams.get !== 'function') return searchParams;

    var variantId = searchParams.get('id');
    if (!variantId) return searchParams;

    var variant = variantCache.get(getNumericId(variantId)) || await fetchVariant(variantId);
    var propertyInputName = 'properties[' + PROPERTY_NAME + ']';

    if (isSaleVariant(variant)) {
      searchParams.set(propertyInputName, getPropertyValue(variant));
    } else {
      searchParams.delete(propertyInputName);
    }

    return searchParams;
  }

  async function enhanceBody(body) {
    if (!body) return body;

    if (body instanceof FormData) {
      return enhanceFormData(body);
    }

    if (body instanceof URLSearchParams) {
      return enhanceSearchParams(body);
    }

    if (typeof body === 'string') {
      try {
        return JSON.stringify(await enhanceJsonPayload(JSON.parse(body)));
      } catch (error) {
        var params = new URLSearchParams(body);
        if (params.has('id')) {
          await enhanceSearchParams(params);
          return params.toString();
        }
      }
    }

    return body;
  }

  function isCartAddUrl(resource) {
    var url = typeof resource === 'string' ? resource : resource?.url;
    if (!url) return false;

    try {
      var parsedUrl = new URL(url, window.location.origin);
      return /\/cart\/add(?:\.js)?$/.test(parsedUrl.pathname);
    } catch (error) {
      return false;
    }
  }

  function getRequestMethod(resource, init) {
    return String(init?.method || resource?.method || 'GET').toUpperCase();
  }

  function installFetchWrapper() {
    if (!originalFetch || window.__shopdoenCompareAtFetchWrapped) return;

    window.__shopdoenCompareAtFetchWrapped = true;
    window.fetch = async function (resource, init) {
      var requestInit = init ? Object.assign({}, init) : init;
      var body = requestInit?.body;

      if (isCartAddUrl(resource) && getRequestMethod(resource, requestInit) === 'POST' && body) {
        requestInit.body = await enhanceBody(body);
      }

      return originalFetch(resource, requestInit);
    };
  }

  function installXhrWrapper() {
    if (!window.XMLHttpRequest || window.__shopdoenCompareAtXhrWrapped) return;

    var originalOpen = window.XMLHttpRequest.prototype.open;
    var originalSend = window.XMLHttpRequest.prototype.send;

    window.__shopdoenCompareAtXhrWrapped = true;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__shopdoenCompareAtMethod = method;
      this.__shopdoenCompareAtUrl = url;
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function (body) {
      if (
        isCartAddUrl(this.__shopdoenCompareAtUrl) &&
        String(this.__shopdoenCompareAtMethod || 'GET').toUpperCase() === 'POST' &&
        body
      ) {
        enhanceBody(body)
          .then(function (enhancedBody) {
            originalSend.call(this, enhancedBody);
          }.bind(this))
          .catch(function () {
            originalSend.call(this, body);
          }.bind(this));
        return;
      }

      return originalSend.apply(this, arguments);
    };
  }

  function syncForm(form, variantData) {
    if (!form) return;

    var variant = registerVariant(variantData);
    var inputName = 'properties[' + PROPERTY_NAME + ']';
    var input = form.querySelector('input[name="' + inputName + '"]');

    if (isSaleVariant(variant)) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        form.appendChild(input);
      }

      input.value = getPropertyValue(variant);
    } else if (input) {
      input.remove();
    }
  }

  async function syncFormFromSelectedId(form) {
    var variantId = form?.querySelector?.('[name="id"]')?.value;
    if (!variantId) return;

    syncForm(form, await fetchVariant(variantId));
  }

  function isAddToCartForm(form) {
    if (!form || form.closest('product-form')) return false;

    if (form.matches('[data-type*="add-to-cart-form"]')) return true;
    if (!form.action) return false;

    try {
      var action = new URL(form.action, window.location.origin);
      return /\/cart\/add(?:\.js)?$/.test(action.pathname);
    } catch (error) {
      return false;
    }
  }

  function installSubmitFallback() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!isAddToCartForm(form)) return;

      if (form.dataset.compareAtPriceReady === 'true') {
        delete form.dataset.compareAtPriceReady;
        return;
      }

      event.preventDefault();

      syncFormFromSelectedId(form).finally(function () {
        form.dataset.compareAtPriceReady = 'true';

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      });
    }, true);
  }

  window.ShopdoenCompareAtLineItemProperty = {
    propertyName: PROPERTY_NAME,
    registerVariant: registerVariant,
    registerProduct: registerProduct,
    scan: scan,
    enhanceCartItem: enhanceCartItem,
    enhanceJsonPayload: enhanceJsonPayload,
    enhanceFormData: enhanceFormData,
    syncForm: syncForm,
    syncFormFromSelectedId: syncFormFromSelectedId
  };

  installFetchWrapper();
  installXhrWrapper();
  installSubmitFallback();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          scan(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
